/**
 * Question-level constants and pure helpers shared by SurveyQuestionsPreview and QuestionCard.
 *
 * They live here rather than in either component because both need them: leaving them in the
 * parent would make the child import from its own parent, and a parent<->child cycle is fragile
 * for the sake of nothing. Moved verbatim — no behaviour change.
 */

import type { ParsedQuestion } from '../../components/survey/types';
import type { RapidFireConfig } from '../../utils/surveyApi';
import { UI_TEXT } from '../../config/ui-text';
import { RAPID_FIRE_DEFAULT_DURATION_SECONDS } from './rapidFire';
import { selectableOptionCount } from './multiSelect';
import { isVideoUrl } from './useStimulusReady';

/** Checks if a question has randomize_media_order set (including backward compat with old image_position_mode). */
export function shouldRandomizeMedia(q: ParsedQuestion): boolean {
  if (q.randomize_media_order != null) return q.randomize_media_order;
  return q.image_position_mode === 'randomized';
}

// All supported question types
export const QUESTION_TYPES = [
  { value: 'info_screen', label: 'Information Screen' },
  { value: 'single_choice', label: UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.QUESTION_TYPES.SINGLE_CHOICE },
  { value: 'multiple_choice', label: UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.QUESTION_TYPES.MULTIPLE_CHOICE },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'rating_scale', label: 'Rating Scale' },
  { value: 'likert_scale', label: 'Likert Scale' },
  { value: 'ranking', label: 'Ranking' },
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'nps', label: 'Net Promoter Score' },
  { value: 'numeric_input', label: 'Numeric Input' },
  { value: 'matrix_rating', label: 'Matrix/Grid' },
  { value: 'max_diff', label: 'MaxDiff (Best/Worst)' },
  { value: 'conjoint_analysis', label: 'Conjoint Analysis (Choice-Based)' },
  { value: 'image_hotspot', label: 'Image Hotspot (Click Ranking)' },
  { value: 'percentage_allocation', label: 'Percentage Allocation' },
  { value: 'photo_upload', label: 'Photo Upload (AI Product Extraction)' },
];

// Probing-rubric tiers — mirrors the backend rubric schema (0 = core, 1 = functional, 2 = value).
export const RUBRIC_TIER_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Core' },
  { value: 1, label: 'Functional' },
  { value: 2, label: 'Value' },
];

// Tier a freshly-added rubric point starts at (1 = functional, the common middle tier).
export const DEFAULT_RUBRIC_POINT_TIER = 1;

export const RUBRIC_INFO_TEXT =
  'Leave blank and we will auto-derive a probing rubric for this question when you save. You can edit the derived rubric afterward. The rubric only guides follow-ups when "adaptive probing" is enabled for the survey — it has no effect while that toggle is off.';

export const tierLabel = (tier: number): string =>
  RUBRIC_TIER_OPTIONS.find((t) => t.value === tier)?.label ?? 'Functional';

// Multi-select cap (spec 36). Blank clears the limit; the runtime and the publish validator both
// treat "no value" as "no limit".
export const MAX_SELECTIONS_LABEL = 'Maximum selections';
export const MAX_SELECTIONS_PLACEHOLDER = 'No limit';
export const MAX_SELECTIONS_NONE = 'No limit';
export const MAX_SELECTIONS_HELP =
  'Respondents can pick up to this many options. Leave blank for no limit.';

/** The smallest cap that means anything: "at most 0" is a question nobody can answer. */
export const MIN_MAX_SELECTIONS = 1;

/**
 * The number the author ASKED for, unclamped. Blank or unparseable means "no cap".
 *
 * Kept apart from the stored value so a cap the option count currently forbids is not silently
 * rewritten as the author's own choice — see decisions-02 on the requested/stored split.
 */
export const parseMaxSelections = (raw: string): number | null => {
  const parsed = parseInt(raw, 10);
  return Number.isInteger(parsed) && parsed >= MIN_MAX_SELECTIONS ? parsed : null;
};

/**
 * What actually gets STORED for a requested number: the author's intent, clamped to what the
 * current option count can honour. `undefined` when nothing can bind.
 */
export const storedSelectionCap = (requested: number | null, optionCount?: number): number | null =>
  requested === null || !optionCount ? requested : Math.min(requested, optionCount);

/**
 * The largest cap that could ever bind, or undefined when the question offers no real choice.
 *
 * One fewer than the option count: a cap equal to the number of options permits everything, which
 * the runtime treats as no cap at all. Blank/`other` rows do not count as options.
 */
export const maxSelectableOptions = (question: ParsedQuestion): number | undefined =>
  selectableOptionCount(question) || undefined;

// ONE image per MaxDiff item. A MaxDiff shows four items side by side in two columns, so three
// thumbnails per tile is eight images on screen at once and the comparison stops being readable.
// A visual MaxDiff is one concept per tile (spec 36, decisions-01).
export const MAX_DIFF_ITEM_MEDIA_LIMIT = 1;

// Thumbnail box for a claim's image in read mode (matches the per-choice preview).
export const MAX_DIFF_ITEM_THUMB_CLASS =
  'w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50';

/**
 * True when two MaxDiff claims are the SAME STRING — the one input the runtime cannot tell apart.
 *
 * Exact comparison, because every runtime key is the exact string: `choices.indexOf(item)` returns
 * the FIRST match (both tiles get one artwork) and maxDiffUtils keys its design on distinct
 * strings (one authored row never gets a slot). Normalising the runtime instead would merge two
 * claims a researcher deliberately wrote as two. Blank rows are an unfinished edit, not ambiguity.
 */
export const hasDuplicateMaxDiffClaims = (choices?: string[]): boolean => {
  const seen = new Set<string>();
  for (const choice of choices ?? []) {
    if (!(choice ?? '').trim()) continue;
    if (seen.has(choice)) return true;
    seen.add(choice);
  }
  return false;
};

/**
 * True when two MaxDiff claims READ the same but are stored differently — a likely typo, not an
 * ambiguity the runtime can hit. Advisory only; `hasDuplicateMaxDiffClaims` is the blocking one.
 */
export const hasLookalikeMaxDiffClaims = (choices?: string[]): boolean => {
  const seen = new Set<string>();
  const seenNormalized = new Set<string>();
  for (const choice of choices ?? []) {
    const trimmed = (choice ?? '').trim();
    if (!trimmed) continue;
    if (seen.has(choice)) continue;
    const normalized = trimmed.toLowerCase();
    if (seenNormalized.has(normalized)) return true;
    seen.add(choice);
    seenNormalized.add(normalized);
  }
  return false;
};

/**
 * Every list of urls a claim can hold, from BOTH sidecars — the shared map and the per-group one,
 * because a claim can be illustrated in one arm and bare in the other.
 */
export const maxDiffItemMediaLists = (question: ParsedQuestion): string[][] => [
  ...Object.values((question.option_media ?? {}) as Record<string, string[] | undefined>),
  ...Object.values(question.option_group_media ?? {}).flatMap((perGroup) =>
    Object.values(perGroup ?? {}),
  ),
].filter((urls): urls is string[] => Array.isArray(urls));

/** True when a claim stores more images than the respondent will ever be shown. */
export const hasSurplusMaxDiffItemMedia = (question: ParsedQuestion): boolean =>
  maxDiffItemMediaLists(question).some((urls) => urls.length > MAX_DIFF_ITEM_MEDIA_LIMIT);

export const hasAnyUrl = (urls: string[] | undefined): boolean => Array.isArray(urls) && urls.length > 0;

/**
 * The options each group will be served with NO picture, given what is stored right now.
 *
 * Mirrors `_build_questions_for_group` (backend `survey_groups.py`), which ASSIGNS the per-group
 * sidecar over `option_media` instead of merging — so one Group A image silently strips the shared
 * image from every other option in that question, for Group A only. Invisible in the builder, and
 * it splits the sample across two different stimuli. Filed as backend#700; the warning is what
 * stands between a researcher and a study they cannot tell is wrong.
 */
export const optionGroupMediaGaps = (
  question: ParsedQuestion,
  groupCount: number,
): { groupIndex: number; optionIndexes: number[] }[] => {
  if (groupCount <= 1) return [];
  const shared = (question.option_media ?? {}) as Record<string, string[] | undefined>;
  const perOption = question.option_group_media ?? {};

  // Every option the researcher has illustrated somewhere — the ones that are supposed to have a
  // picture. An option nobody gave an image to is not a gap, it is a text-only option.
  const illustrated = new Set<string>([
    ...Object.entries(shared).filter(([, urls]) => hasAnyUrl(urls)).map(([index]) => index),
    ...Object.entries(perOption)
      .filter(([, byGroup]) => Object.values(byGroup ?? {}).some(hasAnyUrl))
      .map(([index]) => index),
  ]);
  if (illustrated.size === 0) return [];

  const visibleGroups = question.visible_groups ?? Array.from({ length: groupCount }, (_, i) => i);
  return visibleGroups
    .map((groupIndex) => {
      // The backend keys off PRESENCE of the group in the map, so an option holding an empty list
      // for this group still triggers the wholesale replace while contributing no picture.
      const replacesSharedMedia = Object.values(perOption)
        .some((byGroup) => byGroup != null && String(groupIndex) in byGroup);
      if (!replacesSharedMedia) return { groupIndex, optionIndexes: [] };
      const optionIndexes = [...illustrated]
        .filter((index) => !hasAnyUrl(perOption[index]?.[String(groupIndex)]))
        .map(Number)
        .sort((a, b) => a - b);
      return { groupIndex, optionIndexes };
    })
    .filter((gap) => gap.optionIndexes.length > 0);
};

/**
 * The one media item a respondent's tile will render for a claim: the first that is NOT a video.
 * Mirrors SurveySession's selection exactly, so the builder's thumbnail and its notes can never
 * promise the researcher a rendering the respondent does not get. An all-video claim keeps its
 * first item on show — nothing will render for the respondent, and that is precisely what the
 * researcher has to be able to see and replace.
 */
export const shownMaxDiffItemMedia = (urls: string[]): string[] => {
  const shown = urls.find((url) => url && !isVideoUrl(url));
  return shown ? [shown] : urls.slice(0, MAX_DIFF_ITEM_MEDIA_LIMIT);
};

/**
 * True when a claim holds media but NONE of it is an image — the only case the MaxDiff tile cannot
 * render, so it is worth saying at authoring time rather than at fielding time.
 */
export const hasMaxDiffItemVideo = (question: ParsedQuestion): boolean =>
  maxDiffItemMediaLists(question).some(
    (urls) => urls.length > 0 && urls.every((url) => isVideoUrl(url)),
  );

// Question types that support media content (images/videos)
// Note: image_hotspot and photo_upload have their own specialized media handling
export const MEDIA_SUPPORTED_TYPES = [
  'info_screen',
  'single_choice',
  'multiple_choice',
  'yes_no',
  'rating_scale',
  'likert_scale',
  'ranking',
  'short_text',
  'long_text',
  'dropdown',
  'nps',
  'numeric_input',
  'matrix_rating',
  'max_diff',
  'conjoint_analysis',
  'percentage_allocation',
];

// Per-pool badge colors (cycle when there are many pools)
export const RANDOMIZATION_POOL_BADGE_CLASSES = [
  'bg-violet-100 text-violet-900 border-violet-300',
  'bg-amber-100 text-amber-900 border-amber-300',
  'bg-emerald-100 text-emerald-900 border-emerald-300',
  'bg-sky-100 text-sky-900 border-sky-300',
  'bg-rose-100 text-rose-900 border-rose-300',
  'bg-indigo-100 text-indigo-900 border-indigo-300',
];

export function formatRandomizationPoolLabel(poolId: string): string {
  if (poolId === 'default') return 'Default';
  const m = /^pool_([a-z])$/i.exec(poolId);
  if (m) return `Pool ${m[1].toUpperCase()}`;
  return poolId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function nextRandomizationPoolId(existingIds: string[]): string {
  const lower = new Set(existingIds.map((id) => id.toLowerCase()));
  for (let i = 0; i < 26; i++) {
    const ch = String.fromCharCode(97 + i);
    const id = `pool_${ch}`;
    if (!lower.has(id)) return id;
  }
  return `pool_${Date.now()}`;
}

export function randomizationPoolBadgeClass(poolId: string, orderedPoolIds: string[]): string {
  const idx = orderedPoolIds.indexOf(poolId);
  const i = idx >= 0 ? idx : 0;
  return RANDOMIZATION_POOL_BADGE_CLASSES[i % RANDOMIZATION_POOL_BADGE_CLASSES.length];
}

/** Defaults a block picks up the moment rapid-fire is switched on. Skip is OFF by default: the
 *  ask is that the researcher chooses, and no other section of the platform offers a skip. */
export const DEFAULT_RAPID_FIRE_CONFIG: RapidFireConfig = {
  enabled: true,
  duration_seconds: RAPID_FIRE_DEFAULT_DURATION_SECONDS,
  max_questions: null,
  allow_skip: false,
};

export const RAPID_FIRE_MIN_DURATION_SECONDS = 10;
export const RAPID_FIRE_MAX_DURATION_SECONDS = 600;

export const BLOCK_ACCENT_PALETTES = [
  'border-l-teal-500 bg-teal-50/60',
  'border-l-indigo-500 bg-indigo-50/60',
  'border-l-amber-500 bg-amber-50/60',
  'border-l-rose-500 bg-rose-50/60',
];

export function blockAccentClass(blockId: string | null | undefined): string {
  if (!blockId) return '';
  let h = 0;
  for (let i = 0; i < blockId.length; i++) h = (h + blockId.charCodeAt(i)) % BLOCK_ACCENT_PALETTES.length;
  return `border-l-4 ${BLOCK_ACCENT_PALETTES[h]}`;
}

// Question types that support conditional actions (screener/skip logic)
export const CONDITIONAL_ACTION_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown'];

// Question types that can be used as source for display conditions
export const DISPLAY_CONDITION_TYPES = ['single_choice', 'multiple_choice', 'yes_no', 'dropdown', 'percentage_allocation'];
